import React from 'react';
import PropTypes from 'prop-types';
import { Text, Image } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import { EmergencyHistoryShape } from '../../prop-types/emergency-status';
import { getDateDiffLiteral } from '../../../lib/utils';
import styles from './OutageHistory.module.css';

const cx = classnames.bind(styles);

const reportStatus = {
  true: 'YES',
  false: 'NO',
};

const defaultRecord = {
  image_url: 'https://static.gasbuddy.com/web/consumer/profile/user-icon.svg',
  nick_name: 'N/A',
  update_date: '- - -',
  report_status: 'N/A',
};

const getDateIfValid = (reportDate) => {
  // if input string isn't parseable to Date type, it means
  // that default update date string kicked in. As seen during testing,
  // the service sends empty strings as defaults for missing properties.
  if (Number.isNaN(Date.parse(reportDate))) return reportDate;

  return getDateDiffLiteral(reportDate);
};

const getUserToDisplay = (nickname, stamp) => {
  if (nickname) {
    return nickname;
  }

  return stamp !== 'member' ? stamp : defaultRecord.nick_name;
};

function GridItem({
  as: Element,
  children,
  alignStart,
  className,
}) {
  return (
    <Element
      className={cx(
        'gridItem',
        alignStart ? 'textStart' : 'textCenter',
        { cell: Element === 'td' },
        className,
      )}
    >
      {children}
    </Element>
  );
}

GridItem.propTypes = {
  children: PropTypes.node,
  alignStart: PropTypes.bool,
  as: PropTypes.string,
  className: PropTypes.string,
};

GridItem.defaultProps = {
  as: 'td',
  children: undefined,
  alignStart: false,
  className: undefined,
};

function EmergencyStatusRecord({ record }) {
  return (
    <React.Fragment>
      <GridItem alignStart className={cx('avatarAndUserName')}>
        <Image
          lazy
          src={record.image_url || defaultRecord.image_url}
          alt="Avatar Image"
          className={cx('avatar')}
        />
        {getUserToDisplay(record.nick_name, record.stamp)}
      </GridItem>
      <GridItem>
        {getDateIfValid(record.update_date) || defaultRecord.update_date}
      </GridItem>
      <GridItem>
        <Text bold>
          {reportStatus[record.report_status] || defaultRecord.report_status}
        </Text>
      </GridItem>
    </React.Fragment>
  );
}

EmergencyStatusRecord.propTypes = {
  record: PropTypes.shape({
    poi_id: PropTypes.number,
    update_date: PropTypes.string,
    nick_name: PropTypes.string,
    stamp: PropTypes.string,
    report_type: PropTypes.string,
    report_status: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    image_url: PropTypes.string,
  }),
};

EmergencyStatusRecord.defaultProps = {
  record: defaultRecord,
};

export default function OutageHistory({
  list,
  type,
}) {
  const historyRecords = list[type];

  return historyRecords.length
    ? (
      <table className={cx('container')}>
        <thead>
          <tr>
            <GridItem as="th" className={cx('textStart')}>
              <Text bold>Updated By</Text>
            </GridItem>
            <GridItem as="th">
              <Text bold>Last Updated</Text>
            </GridItem>
            <GridItem as="th">
              <Text bold className={cx('title')}>
                {type.substring(4)}
              </Text>
            </GridItem>
          </tr>
        </thead>
        <tbody>
          {historyRecords.map((item, index) => (
            <tr key={index.toString()}>
              <EmergencyStatusRecord record={item} />
            </tr>
          ))}
        </tbody>
      </table>
    )
    : (
      <Text as="p" color="orange">
        No status history available for this station.
      </Text>
    );
}

OutageHistory.propTypes = {
  list: PropTypes.shape(
    PropTypes.objectOf({
      [PropTypes.string]: PropTypes.arrayOf(EmergencyHistoryShape),
    }),
  ),
  type: PropTypes.oneOf([
    'has_gas',
    'has_diesel',
    'has_power',
  ]).isRequired,
};

OutageHistory.defaultProps = {
  list: [],
};
